import React from "react";
import ReactDOM from "react-dom/client";
import "./index.scss";
import "./next/ui/tokens/tokens.scss";
import { bootPublicPreview, capturePreviewParams } from "./next/public/mode";
import { Provider as StoreProvider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { store } from "./backend/store";
import { PersistBoot } from "./components/base/PersistBoot";
import { registerServiceWorker } from "./serviceWorkerRegistration";
// import * as Sentry from "@sentry/browser";
import './res/languages';
import { App } from "./components/base/App";
import { QueryProvider } from "./next/data/QueryProvider";
import { DirectionProvider } from "./components/base/DirectionProvider";
import { I18nGate } from "./components/base/I18nGate";

import Echo from "laravel-echo";

export const Pusher = require("pusher-js");

const BROADCASTING_AUTH_ENDPOINT = `${process.env.REACT_APP_API_URL ?? ""}/broadcasting/auth`;

export const ECHO = new Echo({
  broadcaster: "pusher",
  // Pass the npm-imported client class explicitly instead of relying on a global
  // `window.Pusher` — that global used to come from a blocking <script> tag in
  // index.html (js.pusher.com), which delayed first paint on every page. This
  // makes that script unnecessary; see index.html for its removal.
  Pusher,
  key: "73f5e5b23ed3dc36d070",
  cluster: "mt1",
  forceTLS: true,
  // Use a custom authorizer so the Bearer token is read fresh from localStorage
  // on every private-channel auth request instead of being frozen at startup.
  authorizer: (channel: any, _options: any) => {
    return {
      authorize: (socketId: string, callback: (error: Error | null, data: any) => void) => {
        const token = localStorage.getItem("token") ?? "";
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);
        fetch(BROADCASTING_AUTH_ENDPOINT, {
          method: "POST",
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({
            socket_id: socketId,
            channel_name: channel.name,
          }),
        })
          .then(async (response) => {
            clearTimeout(timeoutId);
            const text = await response.text();
            if (!response.ok) {
              const err = new Error(
                `Broadcasting auth failed: ${response.status} ${text.slice(0, 200)}`
              );
              console.error("[Echo] Auth response error:", err.message);
              callback(err, null);
              return;
            }
            if (!text) {
              const err = new Error(
                `Broadcasting auth returned empty body (status ${response.status})`
              );
              console.error("[Echo] Auth response error:", err.message);
              callback(err, null);
              return;
            }
            try {
              const data = JSON.parse(text);
              callback(null, data);
            } catch (parseErr) {
              const err = new Error(
                `Broadcasting auth returned non-JSON body (status ${response.status}): ${text.slice(0, 200)}`
              );
              console.error("[Echo] Auth response parse error:", err.message);
              callback(err, null);
            }
          })
          .catch((error: Error) => {
            clearTimeout(timeoutId);
            console.error("[Echo] Private channel auth error:", error);
            callback(error, null);
          });
      },
    };
  },
});

(window as any).Echo = ECHO;

// Sentry.init({
//   dsn: "https://1f3002fc8c264dc4b8f86b65d14c6ddc@o1157038.ingest.sentry.io/4504962637168640",
//   integrations: [new Sentry.BrowserTracing()],

//   tracesSampleRate: 1.0,
// });

// Dev/test builds only: ?public=next|legacy and ?home=W1|W2 preview the unreleased public look (see next/public/mode.ts).
capturePreviewParams(window.location.search);
bootPublicPreview(window.location.search, undefined, window.location.hash);

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <StoreProvider store={store}>
        {/* Waits for the persisted slices to rehydrate so the first paint shows the
            volunteer's last-loaded route instead of flashing empty lists — but
            never waits forever; see PersistBoot. */}
        <QueryProvider>
          <PersistBoot>
            {/* Holds the app (behind the boot splash) until the active language's texts
                have loaded, so no page ever shows raw translation keys. It also removes
                the static splash once the first real frame is ready. */}
            <I18nGate>
              <DirectionProvider>
                <App />
              </DirectionProvider>
            </I18nGate>
          </PersistBoot>
        </QueryProvider>
      </StoreProvider>
    </BrowserRouter>
  </React.StrictMode>
);

// The static pre-JS splash (public/index.html) is removed by <I18nGate> in the same
// commit that first mounts the app with its texts — not here, which would uncover a page
// still waiting for persisted state and translations.

registerServiceWorker();
